/**
 * Utility functions for element calculations in hydroponics applications.
 * Used by the Salt Composition Calculator and related components.
 */

import { ElementComposition, ELEMENT_CATEGORIES, ElementCategory } from '../types/elementComposition';
import { Element } from '../types/Element';

// Common elements in hydroponics with atomic weights
export const ELEMENTS: Record<string, Element> = {
  'N': { symbol: 'N', name: 'Nitrogen', atomicNumber: 7, atomicWeight: 14.0067, category: ELEMENT_CATEGORIES.MACRONUTRIENT },
  'P': { symbol: 'P', name: 'Phosphorus', atomicNumber: 15, atomicWeight: 30.9738, category: ELEMENT_CATEGORIES.MACRONUTRIENT },
  'K': { symbol: 'K', name: 'Potassium', atomicNumber: 19, atomicWeight: 39.0983, category: ELEMENT_CATEGORIES.MACRONUTRIENT },
  'Ca': { symbol: 'Ca', name: 'Calcium', atomicNumber: 20, atomicWeight: 40.078, category: ELEMENT_CATEGORIES.SECONDARY },
  'Mg': { symbol: 'Mg', name: 'Magnesium', atomicNumber: 12, atomicWeight: 24.305, category: ELEMENT_CATEGORIES.SECONDARY },
  'S': { symbol: 'S', name: 'Sulfur', atomicNumber: 16, atomicWeight: 32.065, category: ELEMENT_CATEGORIES.SECONDARY },
  'Fe': { symbol: 'Fe', name: 'Iron', atomicNumber: 26, atomicWeight: 55.845, category: ELEMENT_CATEGORIES.MICRONUTRIENT },
  'Mn': { symbol: 'Mn', name: 'Manganese', atomicNumber: 25, atomicWeight: 54.938, category: ELEMENT_CATEGORIES.MICRONUTRIENT },
  'Zn': { symbol: 'Zn', name: 'Zinc', atomicNumber: 30, atomicWeight: 65.38, category: ELEMENT_CATEGORIES.MICRONUTRIENT },
  'Cu': { symbol: 'Cu', name: 'Copper', atomicNumber: 29, atomicWeight: 63.546, category: ELEMENT_CATEGORIES.MICRONUTRIENT },
  'B': { symbol: 'B', name: 'Boron', atomicNumber: 5, atomicWeight: 10.811, category: ELEMENT_CATEGORIES.MICRONUTRIENT },
  'Mo': { symbol: 'Mo', name: 'Molybdenum', atomicNumber: 42, atomicWeight: 95.96, category: ELEMENT_CATEGORIES.MICRONUTRIENT },
  'Cl': { symbol: 'Cl', name: 'Chlorine', atomicNumber: 17, atomicWeight: 35.453, category: ELEMENT_CATEGORIES.MICRONUTRIENT },
  'Na': { symbol: 'Na', name: 'Sodium', atomicNumber: 11, atomicWeight: 22.9897, category: ELEMENT_CATEGORIES.OTHER },
  'O': { symbol: 'O', name: 'Oxygen', atomicNumber: 8, atomicWeight: 15.9994, category: ELEMENT_CATEGORIES.OTHER },
  'H': { symbol: 'H', name: 'Hydrogen', atomicNumber: 1, atomicWeight: 1.00784, category: ELEMENT_CATEGORIES.OTHER },
  'C': { symbol: 'C', name: 'Carbon', atomicNumber: 6, atomicWeight: 12.0107, category: ELEMENT_CATEGORIES.OTHER },
  'Si': { symbol: 'Si', name: 'Silicon', atomicNumber: 14, atomicWeight: 28.0855, category: ELEMENT_CATEGORIES.OTHER },
  'Co': { symbol: 'Co', name: 'Cobalt', atomicNumber: 27, atomicWeight: 58.9332, category: ELEMENT_CATEGORIES.MICRONUTRIENT },
  'Ni': { symbol: 'Ni', name: 'Nickel', atomicNumber: 28, atomicWeight: 58.6934, category: ELEMENT_CATEGORIES.MICRONUTRIENT },
};

/**
 * Calculate molecular weight of a salt based on its element composition
 * @param elements Array of element compositions with percentages
 * @returns Total molecular weight or null if elements are missing data
 */
export const calculateMolecularWeight = (elements: ElementComposition[]): number | null => {
  if (!elements || elements.length === 0) {
    return null;
  }

  let totalWeight = 0;
  
  for (const elem of elements) {
    const elementInfo = ELEMENTS[elem.symbol];
    if (!elementInfo) {
      console.warn(`Element ${elem.symbol} not found in database`);
      return null;
    }
    
    // Add element's contribution to the total weight based on its percentage
    totalWeight += (elementInfo.atomicWeight * elem.percentage / 100);
  }
  
  return totalWeight;
};

/**
 * Calculate the stock solution concentration based on salt solubility
 * @param solubility Solubility in g/L at 20°C
 * @param volumeInLiters Volume of stock solution in liters
 * @returns Maximum concentration in grams
 */
export const calculateStockSolutionConcentration = (
  solubility: number, 
  volumeInLiters: number
): number => {
  return solubility * volumeInLiters;
};

/**
 * Group elements by their nutrient category
 * @param elements Array of element compositions
 * @returns Object with elements grouped by category
 */
export const groupElementsByCategory = (elements: ElementComposition[]): Record<string, ElementComposition[]> => {
  const result: Record<string, ElementComposition[]> = {
    [ELEMENT_CATEGORIES.MACRONUTRIENT]: [],
    [ELEMENT_CATEGORIES.SECONDARY]: [],
    [ELEMENT_CATEGORIES.MICRONUTRIENT]: [],
    [ELEMENT_CATEGORIES.OTHER]: [],
  };
  
  elements.forEach(elem => {
    const elementInfo = ELEMENTS[elem.symbol];
    if (elementInfo) {
      const category = elementInfo.category || ELEMENT_CATEGORIES.OTHER;
      result[category].push({
        ...elem,
        atomicWeight: elementInfo.atomicWeight
      });
    } else {
      result[ELEMENT_CATEGORIES.OTHER].push(elem);
    }
  });
  
  return result;
};

/**
 * Format NPK values from element composition
 * @param elements Array of element compositions
 * @returns NPK string in format "N-P-K"
 */
export const formatNPKString = (elements: ElementComposition[]): string => {
  const n = elements.find(e => e.symbol === 'N')?.percentage.toFixed(1) || '0.0';
  const p = elements.find(e => e.symbol === 'P')?.percentage.toFixed(1) || '0.0';
  const k = elements.find(e => e.symbol === 'K')?.percentage.toFixed(1) || '0.0';
  
  return `${n}-${p}-${k}`;
};

/**
 * Check if the total percentage of elements is valid (close to 100%)
 * @param elements Array of element compositions
 * @param tolerance Allowed deviation from 100% (default 5%)
 * @returns Boolean indicating if composition is valid
 */
export const isValidComposition = (elements: ElementComposition[], tolerance = 5): boolean => {
  if (!elements || elements.length === 0) return false;
  
  const total = elements.reduce((sum, elem) => sum + elem.percentage, 0);
  return Math.abs(total - 100) <= tolerance;
};

// Re-export ELEMENT_CATEGORIES for convenience
export { ELEMENT_CATEGORIES } from '../types/elementComposition';

export default {
  calculateMolecularWeight,
  calculateStockSolutionConcentration,
  groupElementsByCategory,
  formatNPKString,
  isValidComposition,
  ELEMENTS
};
